import type { ReactNode } from "react";
import { ArrowLeft, ArrowRight, LoaderCircle, X } from "lucide-react";

export interface InspectorPanelProps {
  sheetName: string;
  position: number;
  total: number;
  loading: boolean;
  error: boolean;
  onNext: () => void;
  onPrevious: () => void;
  previousDisabled: boolean;
  onRetry: () => void;
  onClose: () => void;
  footerContent?: ReactNode;
}

export function DatabookInspectorPanel({
  kind, sheetName, position, total, loading, error, onNext, onPrevious, previousDisabled,
  onRetry, onClose, children, headerAction, actionError, footerContent,
}: InspectorPanelProps & { kind: "variances" | "qna"; children: ReactNode; headerAction?: ReactNode; actionError?: string | null }) {
  const qna = kind === "qna";
  const title = qna ? "Q&A Thread" : "Variances";
  const noun = qna ? "questions" : "variances";
  const current = position >= 0 && position < total;
  const complete = total > 0 && position === total;
  return (
    <aside id={qna ? "databook-qna-panel" : "databook-variance-panel"}
      aria-label={`${qna ? title : "Variance explanations"} for ${sheetName}`}
      className="flex h-full min-w-0 flex-col bg-card">
      <header className="flex shrink-0 items-start justify-between gap-2 border-b border-border px-5 py-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">{title}</h2>
          <p className="mt-0.5 truncate text-xs text-muted-foreground" title={sheetName}>{sheetName}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
        {headerAction}
        <button type="button" aria-label={qna ? "Close Q&A" : "Close variance explanations"} onClick={onClose}
          className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <X aria-hidden="true" size={16} />
        </button>
        </div>
      </header>
      {actionError && <p role="alert" className="px-5 py-2 text-xs text-destructive">{actionError}</p>}
      <div key={`${sheetName}:${position}`} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5" aria-live="polite">
        {error ? (
          <div className="space-y-3 py-5 text-sm text-muted-foreground" role="alert">
            <p>{title} could not be loaded.</p>
            <button type="button" onClick={onRetry}
              className="rounded-md border border-border px-3 py-2 text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              Try again
            </button>
          </div>
        ) : loading ? (
          <p className="flex items-center gap-2 py-5 text-sm text-muted-foreground" role="status">
            <LoaderCircle aria-hidden="true" size={16} className="animate-spin" /> Loading {noun}…
          </p>
        ) : complete ? (
          <p className="py-5 text-sm text-muted-foreground">All {noun} viewed.</p>
        ) : current ? children : (
          <p className="py-5 text-sm text-muted-foreground">
            {total ? `No ${noun} on this sheet.` : `No saved ${noun} in this databook.`}
          </p>
        )}
      </div>
      {footerContent}
      {!loading && !error && total > 0 && (
        <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
          <span className="text-xs text-muted-foreground" aria-live="polite">
            {current ? `${position + 1} of ${total}` : `${total} ${total === 1 ? noun.slice(0, -1) : noun}`}
          </span>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button type="button" onClick={onPrevious} disabled={previousDisabled}
              className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
              <ArrowLeft aria-hidden="true" size={14} />
              {current || complete ? "Previous" : "Go to previous"}
            </button>
            <button type="button" onClick={onNext}
              className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              {complete ? "Back to first" : current ? "Next" : "Go to next"}
              <ArrowRight aria-hidden="true" size={14} />
            </button>
          </div>
        </footer>
      )}
    </aside>
  );
}
